import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { avatarSrc, useBdQuery } from "../hooks/useBdApi";
import { KpiStrip, StatList } from "../components/Kpi";
import { TrendChart, BreakdownDonut } from "../components/Charts";
import DataTable from "../components/DataTable";
import { AgpPaceStrip } from "../agp/components/AgpPaceStrip";
import { Avatar, Card, ErrorState, Pill, Skeleton, Tabs, TeamDot } from "../components/Ui";
import SourceTip, { TitleWithSource } from "../components/SourceTip";
import { CallReportsTable, DealsTable } from "../components/ReportTables";
import {
  CLIENT_STATUS_TONE,
  MEETING_STATUS_TONE,
  POINT_TYPES,
  PRIMARY_KPIS,
  clientSegmentsSub,
  formatCompactCurrency,
  formatCurrency,
  formatDate,
  formatDecimal,
  formatNumber,
  formatPointDate,
  healthTone,
  moneyOrDash,
  teamColor,
} from "../metrics";

const typeColor = (type) => (POINT_TYPES.find((t) => t.type === type) || {}).color;

export default function MemberPage({ period }) {
  const { sfUserId } = useParams();
  const { data, loading, error, refetch } = useBdQuery(`members/${sfUserId}`, period.apiParams);
  const [tab, setTab] = useState("points");
  const [pointTypeFilter, setPointTypeFilter] = useState("all");

  const points = useMemo(() => {
    if (!data) return [];
    return data.points.filter((p) => pointTypeFilter === "all" || p.type === pointTypeFilter);
  }, [data, pointTypeFilter]);

  if (error) return <ErrorState message={error} onRetry={refetch} />;

  const member = data ? data.member : null;
  const kpis = data ? data.kpis : null;
  const comparison = data ? data.comparison : null;
  const color = teamColor(member ? member.team : "");
  const presentTypes = data ? POINT_TYPES.filter((t) => data.kpis.byType[t.type] && data.kpis.byType[t.type].count) : [];

  return (
    <div className="bd-page" style={{ "--team-color": color }}>
      <div className="bd-member-head">
        {member ? (
          <>
            <Avatar name={member.name} team={member.team} size="xl" src={avatarSrc(member)} />
            <div className="bd-member-head__text">
              <div className="bd-eyebrow">
                {member.team ? (
                  <Link to={`/landmark/bd/team/${member.teamKey}${period.search}`} className="bd-link">
                    <TeamDot team={member.team} /> {member.team}
                  </Link>
                ) : (
                  <span className="bd-muted">No BD team</span>
                )}
              </div>
              <h1>
                {member.name}
                {!member.isActive && (
                  <Pill tone="neutral" className="bd-ml-2">
                    inactive
                  </Pill>
                )}
              </h1>
              <p className="bd-page__lede">
                {[member.title, member.roleName].filter(Boolean).join(" · ") || "—"}
                {member.email && (
                  <>
                    {" · "}
                    <a href={`mailto:${member.email}`} className="bd-link">
                      {member.email}
                    </a>
                  </>
                )}
              </p>
            </div>
            <div className="bd-member-head__side">
              {data.teamRank && (
                <div className="bd-rank-badge">
                  <span className="bd-rank-badge__num">#{data.teamRank.rank}</span>
                  <span className="bd-rank-badge__of">of {data.teamRank.of} in team</span>
                  <small>by point value</small>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <Skeleton width={64} height={64} radius={32} />
            <div className="bd-member-head__text">
              <Skeleton width={240} height={28} />
              <Skeleton width={180} height={14} />
            </div>
          </>
        )}
      </div>

      <KpiStrip
        kpis={kpis}
        comparison={comparison}
        definitions={PRIMARY_KPIS}
        loading={loading && !data}
        subs={{
          pointValue: kpis ? `${formatNumber(kpis.points)} points` : null,
          bluesBooked: kpis ? `${formatNumber(kpis.clientMeetings)} client · ${formatNumber(kpis.ppClientMeetings)} PP` : null,
          tapsBooked: kpis ? `${formatNumber(kpis.tapsHeld)} TAPs held` : null,
          meetingsHappened: kpis ? `${formatNumber(kpis.tapsHeld)} TAPs · ${formatNumber(kpis.callsHeld)} calls` : null,
          lmvHeld: kpis ? `${formatNumber(kpis.lmv)} LMVs booked` : null,
          eventsHeld: "as MC",
          bdDealsClosed: kpis && kpis.bdDealsAmount ? moneyOrDash(kpis.bdDealsAmount) : null,
          liveClients: kpis ? `${clientSegmentsSub(kpis)} · as account manager` : null,
        }}
      />

      <AgpPaceStrip sfUserId={sfUserId} />

      <div className="bd-grid bd-grid--2-1">
        <Card title={<TitleWithSource metric="activityTrend">Activity over time</TitleWithSource>}>
          {loading && !data ? <Skeleton height={280} /> : <TrendChart trend={data.trend} granularity={data.period.granularity} />}
        </Card>
        <Card title={<TitleWithSource metric="pointsByType">Points by type</TitleWithSource>}>
          {loading && !data ? (
            <Skeleton height={220} />
          ) : (
            <>
              <BreakdownDonut byType={kpis.byType} height={190} />
              <div className="bd-card__divider" />
              <StatList
                items={[
                  { label: "TAPs held", metric: "tapsHeld", value: formatNumber(kpis.tapsHeld), comparison: comparison && comparison.tapsHeld },
                  { label: "Other calls held", metric: "callsHeld", value: formatNumber(kpis.callsHeld), comparison: comparison && comparison.callsHeld },
                  { label: "Meetings received", metric: "meetingsScheduled", value: formatNumber(kpis.meetingsReceived), comparison: comparison && comparison.meetingsReceived },
                  { label: "No shows", metric: "meetingsNoShow", value: formatNumber(kpis.meetingsNoShow), comparison: comparison && comparison.meetingsNoShow, invert: true },
                  { label: "Lost / kicked back", metric: "meetingsLost", value: formatNumber(kpis.meetingsLost), comparison: comparison && comparison.meetingsLost, invert: true },
                  { label: "Open opportunities", metric: "pipeline", value: formatNumber(kpis.pipelineCount) },
                  { label: "Open opp value", metric: "pipeline", value: kpis.pipelineAmount ? formatCompactCurrency(kpis.pipelineAmount) : "—" },
                  { label: "BD clients managed", metric: "bdClients", value: formatNumber(data.clients.filter((c) => c.isBdActive).length) },
                ]}
              />
            </>
          )}
        </Card>
      </div>

      <Card
        flush
        title={
          <Tabs
            tabs={[
              { id: "points", label: "Points", count: data ? data.points.length : undefined },
              { id: "calls", label: "Call reports", count: data ? (data.callReports || []).length : undefined },
              { id: "meetings", label: "Meetings", count: data ? data.meetings.length : undefined },
              { id: "deals", label: "Deals", count: data ? data.deals.won.length : undefined },
              { id: "clients", label: "Clients", count: data ? data.clients.length : undefined },
            ]}
            active={tab}
            onChange={setTab}
          />
        }
        actions={
          <>
            {tab === "points" && presentTypes.length > 1 && (
              <div className="bd-chip-row">
                <button type="button" className={`bd-chip ${pointTypeFilter === "all" ? "is-active" : ""}`} onClick={() => setPointTypeFilter("all")}>
                  All
                </button>
                {presentTypes.map((t) => (
                  <button key={t.type} type="button" className={`bd-chip ${pointTypeFilter === t.type ? "is-active" : ""}`} onClick={() => setPointTypeFilter(t.type)}>
                    <i className="bd-chip__dot" style={{ background: t.color }} />
                    {t.short}
                  </button>
                ))}
              </div>
            )}
            {tab === "points" && <SourceTip metric="recentActivity" title="Points" />}
            {tab === "calls" && <SourceTip metric="callReports" title="Call reports" />}
            {tab === "meetings" && <SourceTip metric="meetingsTable" title="Meetings" />}
            {tab === "deals" && <SourceTip metric="dealsWon" title="Deals" />}
            {tab === "clients" && <SourceTip metric="clientsTable" title="Clients" />}
          </>
        }
      >
        {tab === "calls" && <CallReportsTable rows={data ? data.callReports || [] : []} loading={loading && !data} search={period.search} />}
        {tab === "points" && (
          <DataTable
            loading={loading && !data}
            rows={points}
            rowKey={(r) => r.sfId}
            pageSize={25}
            defaultSort={{ key: period.pointDateField, dir: "desc" }}
            emptyTitle="No points in this period"
            columns={[
              {
                key: period.pointDateField,
                label: period.dateBasis === "accredited" ? "Accredited" : "Booked",
                width: 110,
                render: (r) => formatPointDate(r, period.pointDateField, { year: false }),
                sortValue: (r) => r[period.pointDateField],
              },
              { key: "type", label: "Type", width: 160, render: (r) => <Pill color={typeColor(r.type)}>{r.type}</Pill> },
              {
                key: "displayName",
                label: "Activity",
                render: (r) => (
                  <span className="bd-person__text">
                    <span className="bd-ellipsis" title={r.displayName}>
                      {r.displayName || r.name}
                    </span>
                    <small>
                      {[r.accountName, r.contact && r.contact.name, r.sourceOpportunity && r.sourceOpportunity.name].filter(Boolean).join(" · ")}
                    </small>
                  </span>
                ),
              },
              {
                key: "status",
                label: "Outcome",
                hideBelow: "md",
                sortable: false,
                render: (r) =>
                  r.callHappened ? (
                    <Pill tone="success">held {r.callTakenOn ? formatDate(r.callTakenOn, { year: false }) : ""}</Pill>
                  ) : r.neverHappened ? (
                    <Pill tone="danger">never happened</Pill>
                  ) : r.sourceOpportunity && r.sourceOpportunity.stage ? (
                    <Pill tone="neutral">{r.sourceOpportunity.stage}</Pill>
                  ) : (
                    <span className="bd-muted">—</span>
                  ),
              },
              {
                key: "amount",
                label: "Opp amount",
                align: "right",
                hideBelow: "lg",
                sortValue: (r) => (r.sourceOpportunity && r.sourceOpportunity.amount) || 0,
                render: (r) => (r.sourceOpportunity && r.sourceOpportunity.amount ? formatCurrency(r.sourceOpportunity.amount) : <span className="bd-muted">—</span>),
              },
              { key: "pointValue", label: "Value", align: "right", width: 70, render: (r) => formatDecimal(r.pointValue) },
            ]}
          />
        )}

        {tab === "meetings" && (
          <DataTable
            loading={loading && !data}
            rows={data ? data.meetings : []}
            rowKey={(r) => r.sfId}
            pageSize={25}
            defaultSort={{ key: "receivedDate", dir: "desc" }}
            emptyTitle="No meetings booked in this period"
            columns={[
              { key: "receivedDate", label: "Received", width: 110, render: (r) => formatDate(r.receivedDate, { year: false }), sortValue: (r) => r.receivedDate },
              {
                key: "name",
                label: "Meeting",
                render: (r) => (
                  <span className="bd-person__text">
                    <span className="bd-ellipsis" title={r.name}>
                      {r.name}
                    </span>
                    <small>{[r.customerContactName, r.customerTitle, r.customerAccountName].filter(Boolean).join(" · ")}</small>
                  </span>
                ),
              },
              { key: "client", label: "Client", hideBelow: "md", sortValue: (r) => r.client && r.client.name, render: (r) => (r.client && r.client.name) || <span className="bd-muted">—</span> },
              { key: "status", label: "Status", width: 150, render: (r) => <Pill tone={MEETING_STATUS_TONE[r.status] || "neutral"}>{r.status || "—"}</Pill> },
              { key: "dateScheduledFor", label: "Scheduled for", hideBelow: "md", render: (r) => formatDate(r.dateScheduledFor, { year: false }), sortValue: (r) => r.dateScheduledFor },
              { key: "source", label: "Source", hideBelow: "lg", render: (r) => r.source || <span className="bd-muted">—</span> },
            ]}
          />
        )}

        {tab === "deals" && (
          <div>
            <div className="bd-subsection-title">
              Closed won in period
              {data && data.deals.won.length ? (
                <span className="bd-muted"> · {formatCurrency(data.deals.won.reduce((s, d) => s + (d.amount || 0), 0))} total amount</span>
              ) : null}
            </div>
            <DealsTable rows={data ? data.deals.won : []} loading={loading && !data} search={period.search} pageSize={0} />
            <div className="bd-subsection-title">
              Open opportunities {data && data.deals.pipeline.length >= 200 ? "(top 200 by amount)" : ""} <SourceTip metric="pipeline" title="Open opportunities" />
            </div>
            <DataTable
              loading={loading && !data}
              rows={data ? data.deals.pipeline : []}
              rowKey={(r) => r.sfId}
              pageSize={15}
              dense
              defaultSort={{ key: "amount", dir: "desc" }}
              emptyTitle="No open opportunities"
              stickyHeader={false}
              columns={[
                {
                  key: "name",
                  label: "Opportunity",
                  render: (r) => (
                    <span className="bd-person__text">
                      <span className="bd-ellipsis" title={r.name}>
                        {r.name}
                      </span>
                      <small>{[r.accountName, r.clientName].filter(Boolean).join(" · ")}</small>
                    </span>
                  ),
                },
                { key: "stageName", label: "Stage", width: 170, render: (r) => <Pill tone="neutral">{r.stageName}</Pill> },
                { key: "closeDate", label: "Close date", hideBelow: "md", render: (r) => formatDate(r.closeDate, { year: false }), sortValue: (r) => r.closeDate },
                { key: "amount", label: "Amount", align: "right", render: (r) => (r.amount ? formatCurrency(r.amount) : <span className="bd-muted">—</span>) },
              ]}
            />
          </div>
        )}

        {tab === "clients" && (
          <DataTable
            loading={loading && !data}
            rows={data ? data.clients : []}
            rowKey={(r) => r.sfId}
            defaultSort={{ key: "isBdActive", dir: "desc" }}
            emptyTitle="Not the account manager on any BD clients"
            columns={[
              {
                key: "name",
                label: "Client",
                metric: "clientsTable",
                sortValue: (r) => r.name,
                render: (r) => (
                  <span className="bd-person__text">
                    <span>
                      {r.name}
                      {r.subTeam && (
                        <Pill tone="info" className="bd-ml-1">
                          {r.subTeam}
                        </Pill>
                      )}
                      {!r.isBdActive && (
                        <Pill tone="danger" className="bd-ml-1">
                          terminated {formatDate(r.terminatedDate)}
                        </Pill>
                      )}
                    </span>
                    <small>
                      {r.team}
                      {r.accountManager && r.accountManager.id !== sfUserId && r.accountManager.name ? ` · AM: ${r.accountManager.name}` : " · Account manager"}
                    </small>
                  </span>
                ),
              },
              { key: "status", label: "Status", width: 120, render: (r) => <Pill tone={CLIENT_STATUS_TONE[r.status] || "neutral"}>{r.status || "—"}</Pill> },
              { key: "isBdActive", label: "Active", metric: "bdClients", hideBelow: "lg", render: (r) => (r.isBdActive ? "Yes" : "No"), sortValue: (r) => Number(r.isBdActive) },
              { key: "targetPerMonth", label: "Target / mo", align: "right", hideBelow: "md", render: (r) => (r.targetPerMonth ? formatNumber(r.targetPerMonth) : <span className="bd-muted">—</span>) },
              { key: "occurredThisMonth", label: "Held this mo", align: "right", hideBelow: "md", render: (r) => formatNumber(r.occurredThisMonth) },
              {
                key: "healthScore",
                label: "Health",
                align: "right",
                width: 90,
                render: (r) =>
                  r.healthScore === null || r.healthScore === undefined ? <span className="bd-muted">—</span> : <Pill tone={healthTone(r.healthScore)}>{formatNumber(r.healthScore)}</Pill>,
              },
              { key: "currentContractValue", label: "Contract value", align: "right", hideBelow: "lg", render: (r) => (r.currentContractValue ? formatCurrency(r.currentContractValue) : <span className="bd-muted">—</span>) },
            ]}
          />
        )}
      </Card>
    </div>
  );
}
